import { useCallback, useEffect, useMemo, useState } from 'react'

interface ColumnLayoutOptions {
    storageKey: string
    defaultWidths: Record<string, number>
    minWidth?: number | Record<string, number>
}

interface PersistedLayout<Key extends string> {
    order: Key[]
    widths: Record<string, number>
    collapsed: Key[]
}

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null && !Array.isArray(value)

const readLayout = <Key extends string>(defaultOrder: Key[], options: ColumnLayoutOptions): PersistedLayout<Key> => {
    const defaults = { order: defaultOrder, widths: options.defaultWidths, collapsed: [] as Key[] }
    if (typeof window === 'undefined') return defaults

    try {
        const parsed: unknown = JSON.parse(window.localStorage.getItem(options.storageKey) ?? 'null')
        if (!isRecord(parsed)) return defaults
        const isKnownKey = (value: unknown): value is Key => typeof value === 'string' && defaultOrder.some((key) => key === value)
        const storedOrder = Array.isArray(parsed.order) ? parsed.order.filter(isKnownKey) : []
        // A key the saved order has never seen (a newly added column) is inserted at its default
        // position relative to neighbors the saved order DOES know, rather than appended to the end —
        // so it lands where the column list defines it instead of trailing behind unrelated columns.
        const order = defaultOrder.reduce<Key[]>((result, key) => {
            if (storedOrder.includes(key)) return result
            const defaultIndex = defaultOrder.indexOf(key)
            const nextKnownAfter = defaultOrder.slice(defaultIndex + 1).find((candidate) => storedOrder.includes(candidate))
            const insertAt = nextKnownAfter ? result.indexOf(nextKnownAfter) : result.length
            return [...result.slice(0, insertAt), key, ...result.slice(insertAt)]
        }, storedOrder)
        const collapsed = Array.isArray(parsed.collapsed) ? parsed.collapsed.filter(isKnownKey) : []
        const widths = { ...options.defaultWidths }

        if (isRecord(parsed.widths)) {
            const storedWidths = parsed.widths
            defaultOrder.forEach((key) => {
                const width = storedWidths[key]
                if (typeof width === 'number' && Number.isFinite(width)) widths[key] = width
            })
        }
        return { order, widths, collapsed }
    } catch {
        return defaults
    }
}

export const useColumnLayout = <Key extends string>(defaultOrder: Key[], options: ColumnLayoutOptions) => {
    const { storageKey, defaultWidths, minWidth } = options
    const getMinWidth = useMemo(() => {
        if (typeof minWidth === 'number') return () => minWidth
        if (minWidth) return (key: Key) => minWidth[key] ?? 0
        return () => 0
    }, [minWidth])
    const [layout, setLayout] = useState<PersistedLayout<Key>>(() => readLayout(defaultOrder, options))

    useEffect(() => {
        // Saving the layout is a convenience: blocked or full storage (Safari private mode, quota) must
        // never take the page down with it.
        try {
            window.localStorage.setItem(storageKey, JSON.stringify(layout))
        } catch {
            // the layout simply is not remembered
        }
    }, [layout, storageKey])

    const reorderColumn = useCallback((from: Key, to: Key) => {
        setLayout((current) => {
            if (from === to) return current
            const next = [...current.order]
            const fromIndex = next.indexOf(from)
            const toIndex = next.indexOf(to)
            if (fromIndex < 0 || toIndex < 0) return current
            next.splice(fromIndex, 1)
            next.splice(toIndex, 0, from)
            return { ...current, order: next }
        })
    }, [])

    const resizeColumn = useCallback(
        (key: Key, width: number) => {
            setLayout((current) => ({ ...current, widths: { ...current.widths, [key]: Math.max(getMinWidth(key), Math.round(width)) } }))
        },
        [getMinWidth]
    )

    const getWidth = useCallback(
        (key: Key) => Math.max(getMinWidth(key), layout.widths[key] ?? defaultWidths[key] ?? 0),
        [defaultWidths, getMinWidth, layout.widths]
    )
    const isCollapsed = useCallback((key: Key) => layout.collapsed.includes(key), [layout.collapsed])
    const toggleCollapsed = useCallback((key: Key) => {
        setLayout((current) => ({
            ...current,
            collapsed: current.collapsed.includes(key) ? current.collapsed.filter((item) => item !== key) : [...current.collapsed, key],
        }))
    }, [])

    return {
        state: { order: layout.order, isCollapsed },
        dimensions: { getWidth, getMinWidth },
        actions: { reorderColumn, resizeColumn, toggleCollapsed },
    }
}
